import type { ErrorTrackResDto, StackTraceType } from './types';

/**
 * 获取文件的 source map URL
 * @param filePath 文件路径
 * @returns source map URL
 */
export async function getSourceMapUrl(filePath: string): Promise<string | null> {
  try {
    // 如果是完整的 URL，直接尝试获取 .map 文件
    if (filePath.startsWith('http') || filePath.startsWith('https')) {
      return `${filePath}.map`;
    }

    // 如果是相对路径，尝试从当前域名获取
    const baseUrl = window.location.origin;
    // 检查是否已经包含 .js 扩展名
    const jsFile = filePath.endsWith('.js') ? filePath : `${filePath}.js`;

    // 尝试从文件本身中查找 source map 注释
    try {
      const fileUrl = filePath.startsWith('/') ? `${baseUrl}${filePath}` : `${baseUrl}/${filePath}`;
      const response = await fetch(fileUrl);
      if (response.ok) {
        const fileContent = await response.text();
        const sourceMapMatch = fileContent.match(/\/\/# sourceMappingURL=(.+?)(?:$)/);
        if (sourceMapMatch) {
          const sourceMappingURL = sourceMapMatch[1].trim();
          // 如果是相对路径，转换为绝对路径
          if (sourceMappingURL.startsWith('/')) {
            return `${baseUrl}${sourceMappingURL}`;
          } else if (!sourceMappingURL.startsWith('http')) {
            // 相对于当前JS文件的路径
            const filePathDir = filePath.substring(0, filePath.lastIndexOf('/') + 1);
            return `${baseUrl}/${filePathDir}${sourceMappingURL}`;
          }
          return sourceMappingURL;
        }
      }
    } catch (e) {
      console.debug('Failed to fetch file content for source map URL:', e);
    }

    // 如果无法从文件内容中获取，尝试默认的 .map 文件路径
    return `${baseUrl}${jsFile}.map`;
  } catch (e) {
    console.debug('Failed to get source map URL:', e);
    return null;
  }
}

/**
 * 处理堆栈信息，尝试使用 sourceMap 解析
 * @param stack 堆栈信息
 * @param sourceMapEnable 是否启用 sourceMap
 * @param env 环境变量
 * @returns 处理后的堆栈信息
 */
export async function processStackTrace(
  stack: string,
  sourceMapEnable: boolean,
  env?: string,
  message = '未知错误',
): Promise<ErrorTrackResDto> {
  // 如果未启用 sourceMap，直接返回原始堆栈
  if (!sourceMapEnable) {
    return {
      message,
      stack,
      type: 'JS Error',
      env,
    };
  }

  try {
    const stackLines = stack.split('\n');
    const processedLines: string[] = [];
    const sourceMapCache = new Map<string, any>();

    // 处理每一行堆栈信息
    for (const line of stackLines) {
      const match = extractStackLineInfo(line);
      if (!match) continue;

      const [, filePath, lineNum, colNum] = match;

      const processedLineData = await processStackLine(filePath, lineNum, colNum, sourceMapCache);
      if (processedLineData) {
        processedLines.push(processedLineData);
      }
    }
    return {
      message,
      stack: processedLines.join('\n') || stack,
      type: 'JS Error',
      env,
    };
  } catch (e) {
    console.debug('Error processing stack trace:', e);
    return {
      message,
      stack,
      type: 'JS Error',
      env,
    };
  }
}

// 提取堆栈行信息
function extractStackLineInfo(line: string): RegExpMatchArray | null {
  return line.match(/at\s+.*?\s+\((.+?):(\d+):(\d+)\)/) || line.match(/at\s+(.+?):(\d+):(\d+)/);
}

// 处理单个堆栈行
async function processStackLine(
  filePath: string,
  lineNum: string,
  colNum: string,
  sourceMapCache: Map<string, any>,
): Promise<string | null> {
  const sourceMapUrl = await getSourceMapUrl(filePath);
  if (!sourceMapUrl) return null;

  // 获取或缓存 source map 数据
  let sourceMapData = sourceMapCache.get(sourceMapUrl);
  if (!sourceMapData) {
    sourceMapData = await fetchSourceMapData(sourceMapUrl);
    if (sourceMapData) {
      sourceMapCache.set(sourceMapUrl, sourceMapData);
    }
  }

  if (!sourceMapData) return null;

  const { SourceMapConsumer } = await import('source-map-js');
  const consumer = new SourceMapConsumer(sourceMapData);
  try {
    const originalPosition = consumer.originalPositionFor({
      line: Number.parseInt(lineNum, 10),
      column: Number.parseInt(colNum, 10),
    });
    // 跳过 node_modules 和 tracking 相关的文件
    if (
      !originalPosition?.source ||
      originalPosition.source.includes('node_modules') ||
      originalPosition.source.includes('tracking')
    ) {
      return null;
    }

    // 获取源代码上下文
    const sourceContext = getSourceContext(consumer, originalPosition);
    const source = originalPosition.source.replace(/\..\//g, '');
    return `源文件：${source}\n错误代码行列：${originalPosition.line}:${originalPosition.column}\n具体代码：\n${sourceContext}`;
  } catch (error) {
    consumer.destroy();
    console.log('source-err', error);
    return null;
  }
}

// 获取 source map 数据
async function fetchSourceMapData(sourceMapUrl: string): Promise<any | null> {
  try {
    const response = await fetch(sourceMapUrl);
    if (!response.ok) return null;

    const sourceMapData = await response.json();

    // 如果没有 sourcesContent，尝试获取源文件内容
    if (
      !sourceMapData.sourcesContent &&
      sourceMapData.sources &&
      sourceMapData.sources.length > 0
    ) {
      // 先获取所有源文件内容
      const sourcesContent = await Promise.all(
        sourceMapData.sources.map(async (source: string) => {
          try {
            const sourceUrl = sourceMapData.sourceRoot
              ? new URL(source, sourceMapData.sourceRoot).toString()
              : new URL(source, new URL(sourceMapUrl).origin).toString();

            const response2 = await fetch(sourceUrl);
            return response2.ok ? await response2.text() : null;
          } catch (e) {
            console.log(`Error fetching source content for: ${source}`, e);
            return null;
          }
        }),
      );
      // 所有异步操作完成后，再赋值
      sourceMapData.sourcesContent = {
        ...sourceMapData.sourcesContent,
        ...sourcesContent,
      };
    }

    return sourceMapData;
  } catch (e) {
    console.debug('Failed to fetch source map data:', e);
    return null;
  }
}

/**
 * 处理单个堆栈项
 */
async function processStackItem(
  item: StackTraceType,
  sourceMapCache: Map<string, any>,
): Promise<string | null> {
  const { fileName, lineNumber, columnNumber } = item;

  try {
    const sourceMapUrl = await getSourceMapUrl(fileName);
    if (!sourceMapUrl) return null;

    // 获取或缓存 source map 数据
    let sourceMapData = sourceMapCache.get(sourceMapUrl);
    if (!sourceMapData) {
      const response = await fetch(sourceMapUrl);
      if (response.ok) {
        const rawSourceMapData = await response.json();
        sourceMapData = await fetchSourceContent(rawSourceMapData, sourceMapUrl);
        sourceMapCache.set(sourceMapUrl, sourceMapData);
      }
    }

    if (!sourceMapData) return null;

    // 使用 SourceMapConsumer 解析原始位置
    const { SourceMapConsumer } = await import('source-map-js');
    const consumer = new SourceMapConsumer(sourceMapData);

    try {
      const originalPosition = consumer.originalPositionFor({
        line: Number.parseInt(lineNumber.toString(), 10),
        column: Number.parseInt(columnNumber.toString(), 10),
      });

      // 跳过 node_modules 和 tracking 相关的文件
      if (
        !originalPosition?.source ||
        originalPosition.source.includes('node_modules') ||
        originalPosition.source.includes('tracking')
      ) {
        return null;
      }

      // 获取源代码上下文
      const sourceContext = getSourceContext(consumer, originalPosition);
      const source = originalPosition.source.replace(/\..\//g, '');
      return `源文件：${source}\n错误代码行列：${originalPosition.line}:${originalPosition.column}\n具体代码：\n${sourceContext}`;
    } finally {
      consumer.destroy();
    }
  } catch (e) {
    console.debug('Source map processing failed:', e);
    return null;
  }
}

export async function processStackTraceArray(
  error: StackTraceType[],
  sourceMapEnable: boolean,
  env?: string,
  message = '未知错误',
): Promise<ErrorTrackResDto> {
  // 如果未启用 sourceMap，直接返回
  if (!sourceMapEnable) {
    return {
      message,
      stack: '',
      type: 'JS Error',
      env,
    };
  }

  try {
    const sourceMapCache = new Map<string, any>();

    // 并行处理所有堆栈项
    const processedLinePromises = error.map((item) => processStackItem(item, sourceMapCache));
    const processedLines = await Promise.all(processedLinePromises);

    // 过滤掉 null 值并连接结果
    const validLines = processedLines.filter((line): line is string => line !== null);

    return {
      message,
      stack: validLines.join('\n') || '',
      type: 'JS Error',
      env,
    };
  } catch (e) {
    console.debug('Error processing stack trace:', e);
    return {
      message,
      stack: '',
      type: 'JS Error',
      env,
    };
  }
}

// 提取获取源文件内容的逻辑
async function fetchSourceContent(sourceMapData: any, sourceMapUrl: string): Promise<any> {
  if (sourceMapData.sourcesContent || !sourceMapData.sources?.length) return sourceMapData;

  const sourceMapOrigin = new URL(sourceMapUrl).origin;

  const sourcesContent = await Promise.all(
    sourceMapData.sources.map(async (source: string) => {
      try {
        const sourceUrl = sourceMapData.sourceRoot
          ? new URL(source, sourceMapData.sourceRoot).toString()
          : new URL(source, sourceMapOrigin).toString();

        const response = await fetch(sourceUrl);
        return response.ok ? await response.text() : null;
      } catch (e) {
        console.log(`Error fetching source content for: ${source}`, e);
        return null;
      }
    }),
  );

  // 创建新对象，避免直接修改参数
  return {
    ...sourceMapData,
    sourcesContent,
  };
}

// 提取获取源代码上下文的逻辑
function getSourceContext(
  consumer: any,
  originalPosition: { source: string; line?: number },
): string {
  const sourceContent = consumer.sourceContentFor(originalPosition.source);
  if (!sourceContent) return '';

  const sourceLines = sourceContent.split('\n');
  const sourceLine = (originalPosition.line ?? 1) - 1;
  const start = Math.max(0, sourceLine - 2);
  const end = Math.min(sourceLines.length - 1, sourceLine + 2);

  // 使用更简洁的方式生成上下文
  return Array.from({ length: end - start + 1 }, (_, i) => {
    const currentLine = i + start;
    const marker = currentLine === sourceLine ? '>>> ' : '    ';
    return `${marker}${currentLine + 1}: ${sourceLines[currentLine]}`;
  }).join('\n');
}
